import React, { useState, useRef, useEffect, useCallback } from 'react';
import { API_BASE_URL } from '../../constants';
import './Chatbox.css';

// Constants

const KO_VOICE_LANG = 'ko-KR';
const SPEECH_RECOGNITION_LANG = 'ko-KR'; // recognises Korean; change to 'en-US' if you want English input

// Helpers
const timestamp = () =>
  new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

const SpeechRecognitionAPI =
  window.SpeechRecognition || window.webkitSpeechRecognition;

// Main chatbox component
const Chatbox = () => {
  const [messages, setMessages]       = useState([]);
  const [inputText, setInputText]     = useState('');
  const [isLoading, setIsLoading]     = useState(false);
  const [error, setError]             = useState(null);
  const [isListening, setIsListening] = useState(false);
  const [speakingId, setSpeakingId]   = useState(null);
  const [micSupported]                = useState(!!SpeechRecognitionAPI);

  const messagesEndRef = useRef(null);
  const recognitionRef = useRef(null);
  const inputRef       = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  // Claude API call
  const callClaude = useCallback(async (userText) => {
    // Build conversation history for Claude (keep last 20 turns to manage context)
    const history = messages.slice(-20).map((m) => ({
      role: m.isUser ? 'user' : 'assistant',
      content: m.text,
    }));
    history.push({ role: 'user', content: userText });

    const response = await fetch(`${API_BASE_URL}/api/chat`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ messages: history }),
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err?.error?.message || `API error ${response.status}`);
    }

    const data = await response.json();
    console.log('API response:', data); 
    //return data.content?.[0]?.text ?? 'Sorry, I could not generate a response.';
    return data.reply ?? 'Sorry, I could not generate a response.';
  }, [messages]);

  
  const handleSend = useCallback(async (text) => {
    const trimmed = (text ?? inputText).trim();
    if (!trimmed || isLoading) return;

    setIsLoading(true);
    setError(null);
    setInputText('');

    setMessages((prev) => [
      ...prev,
      { id: Date.now(), text: trimmed, isUser: true, time: timestamp() },
    ]);

    try {
      const reply = await callClaude(trimmed);
      setMessages((prev) => [
        ...prev,
        { id: Date.now() + 1, text: reply, isUser: false, time: timestamp() },
      ]);
    } catch (err) {
      console.error('Claude API error:', err);
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
      inputRef.current?.focus();
    }
  }, [inputText, isLoading, callClaude]);

  // Speech Recognition
  const startListening = useCallback(() => {
    if (!SpeechRecognitionAPI) return;

    // Cancel any active recognition
    recognitionRef.current?.abort();

    const recognition = new SpeechRecognitionAPI();
    recognition.lang = SPEECH_RECOGNITION_LANG;
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;
    recognition.continuous = false;

    recognition.onresult = (event) => {
      const transcript = event.results[0]?.[0]?.transcript ?? '';
      if (transcript) {
        setInputText(transcript);
        // Auto-send after voice input
        handleSend(transcript);
      }
    };

    recognition.onerror = (event) => {
      console.warn('Speech recognition error:', event.error);
      setIsListening(false);
    };

    recognition.onend = () => setIsListening(false);

    recognitionRef.current = recognition;
    recognition.start();
    setIsListening(true);
  }, [handleSend]);

  const stopListening = useCallback(() => {
    recognitionRef.current?.stop();
    setIsListening(false);
  }, []);

  const toggleMic = () => (isListening ? stopListening() : startListening());

  // Speech Synthesis
  const speakMessage = useCallback((id, text) => {
    if (speakingId === id) {
      // Stop if already speaking this message
      window.speechSynthesis.cancel();
      setSpeakingId(null);
      return;
    }

    window.speechSynthesis.cancel();

    // Extract Korean text (first block before any English translation)
    // Falls back to full text if no clear split
    const koreanBlock = text.split(/\n(?=[A-Za-z])/)[0].trim();

    const utterance = new SpeechSynthesisUtterance(koreanBlock);
    utterance.lang = KO_VOICE_LANG;
    utterance.rate = 0.9;
    utterance.pitch = 1;

    // Prefer a Korean voice if available
    const voices = window.speechSynthesis.getVoices();
    const koVoice = voices.find((v) => v.lang.startsWith('ko'));
    if (koVoice) utterance.voice = koVoice;

    utterance.onend = () => setSpeakingId(null);
    utterance.onerror = () => setSpeakingId(null);

    setSpeakingId(id);
    window.speechSynthesis.speak(utterance);
  }, [speakingId]);

  // Load voices if browser needs a trigger
  useEffect(() => {
    const loadVoices = () => window.speechSynthesis.getVoices();
    loadVoices();
    window.speechSynthesis.addEventListener('voiceschanged', loadVoices);
    return () => {
      window.speechSynthesis.removeEventListener('voiceschanged', loadVoices);
      window.speechSynthesis.cancel();
    };
  }, []);

  // Cleanup recognition on unmount
  useEffect(() => () => recognitionRef.current?.abort(), []);

  // Render
  return (
    <div className="chatbot-wrapper">
      {/* Header */}
      <div className="chatbot-header">
        <div className="chatbot-header-left">
          <div className="header-avatar">🇰🇷</div>
          <div>
            <div className="header-title">Korean Tutor</div>
            <div className="header-subtitle">한국어 선생님</div>
          </div>
        </div>
        <span className="lang-badge">KO ↔ EN</span>
      </div>

      {/* Messages */}
      <div className="chatbot-messages">
        {messages.length === 0 && !isLoading && (
          <div className="empty-state">
            <div className="empty-state-icon">💬</div>
            <div>Say something in English or Korean to get started.</div>
            <div style={{ fontSize: 12, marginTop: 4 }}>
              {micSupported ? 'Use the mic button or type below.' : 'Type your message below.'}
            </div>
          </div>
        )}

        {messages.map((msg) => (
          <div key={msg.id} className={`message-row ${msg.isUser ? 'user' : 'bot'}`}>
            <div className="message-meta">
              <span>{msg.isUser ? 'You' : '선생님'}</span>
              <span>{msg.time}</span>
            </div>
            <div className="message-bubble">{msg.text}</div>
            {!msg.isUser && (
              <button
                className={`speak-btn ${speakingId === msg.id ? 'speaking' : ''}`}
                onClick={() => speakMessage(msg.id, msg.text)}
                title="Read aloud in Korean"
              >
                {speakingId === msg.id ? '◼ Stop' : '▶ Listen'}
              </button>
            )}
          </div>
        ))}

        {isLoading && (
          <div className="typing-indicator">
            <div className="typing-dot" />
            <div className="typing-dot" />
            <div className="typing-dot" />
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Error banner */}
      {error && (
        <div className="error-banner">
          <span>⚠ {error}</span>
          <button className="error-dismiss" onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}

      {/* Input area */}
      <div className="chatbot-input-area">
        <div className="input-row">
          <input
            ref={inputRef}
            className="chat-input"
            type="text"
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSend()}
            placeholder={isListening ? '듣고 있어요… (Listening…)' : '한국어 or English…'}
            disabled={isLoading || isListening}
          />

          {micSupported && (
            <button
              className={`mic-btn ${isListening ? 'listening' : 'idle'}`}
              onClick={toggleMic}
              disabled={isLoading}
              title={isListening ? 'Stop listening' : 'Speak in Korean'}
            >
              {isListening ? '⏹' : '🎙'}
            </button>
          )}

          <button
            className="send-btn"
            onClick={() => handleSend()}
            disabled={isLoading || !inputText.trim()}
            title="Send"
          >
            ➤
          </button>
        </div>

        <div className="input-hint">
          <div className="hint-dot" />
          <span>
            {micSupported
              ? 'Press 🎙 to speak · ▶ Listen on any reply · Enter to send'
              : 'Enter to send · ▶ Listen on any reply'}
          </span>
        </div>
      </div>
    </div>
  );
};

export default Chatbox;
